/**
 * The catalogue — served by the game-portal server's public, read-only
 * `/api/catalog` endpoint. The shapes below are the app's own; `fetchCatalog`
 * maps the wire format onto them, so the rest of the app never sees the API.
 */

/** The per-game half of the launch contract — constants, not session state. */
export interface LaunchDefaults {
  /** RGS the game talks to once it is open. */
  readonly server: string
  /** The game's id *on the RGS*, which is not the catalogue slug. */
  readonly game: string
  readonly provider: string
  readonly wallet: string
  readonly operator: string
}

/**
 * A `game` is a release: it takes a session, and the launcher hands it one. A
 * `prototype` is a build someone deployed to look at — it has a URL and nothing
 * behind it, so the launcher just opens the URL.
 */
export type GameType = 'game' | 'prototype'

export interface Game {
  /** Catalogue id, and the label under the art placeholder. */
  readonly slug: string
  readonly name: string
  readonly type: GameType
  readonly studio: string
  /** Placeholder figures — no maths certificate exists for this build. */
  readonly rtp: string
  readonly vol: string
  /**
   * Where game clients are deployed. The slug is a path segment under it, so
   * this is a base URL rather than an origin — one host serves the lot.
   */
  readonly baseUrl: string
  /** Card art set in the back office; empty falls back to the placeholder. */
  readonly banner: string
  readonly launch: LaunchDefaults
}

export interface Catalog {
  readonly games: readonly Game[]
  /** Uppercase ISO codes, the way the currency selector shows them. */
  readonly currencies: readonly string[]
  /** RGS-format locale codes (`en_us`) with the native name as label. */
  readonly languages: ReadonlyArray<{ readonly code: string; readonly label: string }>
}

/**
 * Same-origin everywhere: dev goes through the Vite proxy, and in production
 * the load balancer serves /api/* from this same hostname. Override with
 * VITE_API_BASE only when pointing a local build at a remote API.
 */
const API_BASE: string = import.meta.env.VITE_API_BASE ?? ''

interface ApiGame {
  slug: string
  name: string
  type: GameType
  studio: string
  rtp: string
  volatility: string
  baseUrl: string
  rgsServer: string
  rgsGame: string
  provider: string
  wallet: string
  operator: string
  bannerUrl: string
}

interface ApiCatalog {
  games: ApiGame[]
  languages: Array<{ code: string; native: string }>
  currencies: Array<{ code: string }>
}

/** `en-US` (catalogue) → `en_us` (what the RGS expects in the launch URL). */
function rgsLocale(code: string): string {
  return code.toLowerCase().replace(/-/g, '_')
}

/**
 * An uploaded banner is a path on the API (`/api/banners/…`) and follows
 * API_BASE; anything else is already an absolute URL to someone else's host.
 */
function bannerSrc(url: string): string {
  if (!url || /^https?:\/\//.test(url)) return url
  return `${API_BASE}${url}`
}

function toGame(g: ApiGame): Game {
  const prototype = g.type === 'prototype'
  return {
    slug: g.slug,
    name: g.name,
    // A row written before the column existed is a release, and so is one from
    // a server that predates it.
    type: prototype ? 'prototype' : 'game',
    studio: g.studio,
    // Nothing certifies a prototype's maths, because there is no maths yet.
    rtp: prototype ? '—' : `${g.rtp}%`,
    vol: prototype ? 'PROTO' : `${g.volatility} VOL`,
    baseUrl: g.baseUrl,
    banner: bannerSrc(g.bannerUrl),
    launch: {
      server: g.rgsServer,
      game: g.rgsGame,
      provider: g.provider,
      wallet: g.wallet,
      operator: g.operator,
    },
  }
}

/** Thrown on 401 — the session expired; the app drops back to the login page. */
export class UnauthorizedError extends Error {}

export async function fetchCatalog(): Promise<Catalog> {
  const res = await fetch(`${API_BASE}/api/catalog`)
  if (res.status === 401) throw new UnauthorizedError('not signed in')
  if (!res.ok) throw new Error(`catalog request failed (${res.status})`)
  const data = (await res.json()) as ApiCatalog

  return {
    games: data.games.map(toGame),
    currencies: data.currencies.map((c) => c.code),
    languages: data.languages.map((l) => ({ code: rgsLocale(l.code), label: l.native })),
  }
}

export function findGame(games: readonly Game[], slug: string): Game {
  return games.find((game) => game.slug === slug) ?? games[0]
}

/** The two halves of the catalogue the tab strip switches between. */
export function gamesOfType(games: readonly Game[], type: GameType): readonly Game[] {
  return games.filter((game) => game.type === type)
}

export function searchGames(games: readonly Game[], query: string): readonly Game[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return games

  return games.filter(
    (game) =>
      game.name.toLowerCase().includes(needle) || game.studio.toLowerCase().includes(needle),
  )
}
